import React, { ReactNode, useState } from 'react';

import { formatPriceWithCurrency, useModalCloseOnEsc } from '@vb-django/components/minskog/utils';

import { cn } from '@utils/cn';

import { Button, Dialog, Icon, Text } from '@ui-component-library';

import { ConfirmClosePopup } from './components/ConfirmClosePopup';
import { useCheckout } from './context/CheckoutContext';

export const Checkout = ({ children }: { children: ReactNode }) => {
  const {
    isCheckoutOpen,
    currentStep,
    previousStep,
    resetCheckout,
    nextStep,
    selectedProducts,
    setOpenSelectProductDialog,
    orderPrices,
    extraData,
    setOrderStepFieldErrors,
    config
  } = useCheckout();

  const [isConfirmCloseOpen, setIsConfirmCloseOpen] = useState(false);

  const currentKey = config.stepKeys[currentStep - 1];
  const isFirstStep = currentStep === 1;
  const isLastStep = currentStep === config.stepKeys.length;
  const isPaymentStep = currentKey === 'payment';
  const isAccountStep = currentKey === 'account';

  const handleConfirmClose = () => setIsConfirmCloseOpen(true);

  const handleActualClose = () => {
    setIsConfirmCloseOpen(false);
    resetCheckout();
  };

  const handleNextStep = () => {
    if (isFirstStep && selectedProducts.length === 0) {
      setOpenSelectProductDialog(true);
      return;
    }
    if (isFirstStep && config.validateOrderStep) {
      const fieldErrors = config.validateOrderStep({ extraData, selectedProducts });
      if (Object.keys(fieldErrors).length > 0) {
        setOrderStepFieldErrors(fieldErrors);
        return;
      }
      setOrderStepFieldErrors({});
    }
    nextStep();
  };

  useModalCloseOnEsc(isCheckoutOpen, handleConfirmClose);

  const showFooterPrice = Boolean(orderPrices.discountPrice && !isLastStep);

  return (
    <>
      <Dialog isOpen={isCheckoutOpen} size="large" className="md:h-[80dvh]">
        <Dialog.Header className={cn(isLastStep ? 'hidden' : 'px-4 py-3 md:p-6')}>
          <div className="flex w-full items-start justify-between gap-4">
            <div className="flex flex-row items-center gap-4">
              {!isFirstStep && (
                <Button variant="tertiary" size="icon" onClick={previousStep}>
                  <Icon name="ArrowRightSimple" size="large" className="rotate-180" />
                </Button>
              )}
              <div className="flex flex-col gap-1">
                <Text variant={'text-base/leading-normal/medium'}>{config.title}</Text>
                {config.subtitle && (
                  <Text variant="text-sm/leading-normal/normal" className="text-cloud-900">
                    {config.subtitle}
                  </Text>
                )}
              </div>
            </div>
            <Button variant="tertiary" size="icon" onClick={handleConfirmClose}>
              <Icon name="CrossSimple" className="color-charcoal-500" size="large" />
            </Button>
          </div>
        </Dialog.Header>

        <Dialog.Content className="bg-sandstone-dark flex flex-1 flex-col">
          {children}
        </Dialog.Content>

        {!(isPaymentStep || isAccountStep) && (
          <Dialog.Footer>
            <div
              className={cn(
                'flex flex-row gap-2',
                showFooterPrice ? 'justify-between' : 'justify-end'
              )}
            >
              {showFooterPrice && (
                <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-baseline">
                  <Text variant={'text-lg/leading-none/medium'} className="whitespace-nowrap">
                    {formatPriceWithCurrency(orderPrices.discountPrice)}
                  </Text>
                  <Text variant={'text-sm/leading-normal/normal'} className="text-charcoal-300">
                    exkl. moms
                  </Text>
                </div>
              )}

              <div className="flex flex-row gap-4">
                {config.secondaryActions}
                <Button variant="primary" onClick={isLastStep ? handleActualClose : handleNextStep}>
                  {isLastStep ? 'Stäng' : (config.primaryActionLabel ?? 'Nästa steg')}
                </Button>
              </div>
            </div>
          </Dialog.Footer>
        )}
      </Dialog>

      <ConfirmClosePopup
        isOpen={isConfirmCloseOpen}
        setIsOpen={setIsConfirmCloseOpen}
        handleClose={handleActualClose}
      />
    </>
  );
};
